/**
 * Native host bridge.
 *
 * The ESS web app runs inside two different native shells during the Cordova ->
 * Expo migration, and they expose different bridges:
 *
 *   - Expo / react-native-webview  ->  window.ReactNativeWebView.postMessage
 *   - Cordova InAppBrowser         ->  window.webkit.messageHandlers.cordova_iab.postMessage
 *     (Cordova shims that name on Android too — see the plugin's
 *      src/android/InAppBrowser.java:1376)
 *
 * Call sites talk to this module and never touch either bridge directly, so the
 * legacy branch can be deleted in one place once old installs age out.
 *
 * ---------------------------------------------------------------------------
 * Encoding — read before changing anything here.
 *
 * The shipped Cordova app decodes a `print` payload as:
 *
 *     StringToArrayBuffer(atob(content))          (app src/bus.js:openPDF)
 *
 * string-to-arraybuffer *sniffs* its argument rather than trusting the caller:
 * if it looks like base64 it decodes again, otherwise it treats it as a binary
 * string. Historically three call sites double-encoded and two single-encoded,
 * and both survived only because of that sniffing.
 *
 * We always double-encode for the legacy branch. Two atob() calls is the path
 * that decodes deterministically; single-encoding merely *happens* to work
 * because binary bytes fail the is-base64 regex. The modern branch declares
 * `encoding` explicitly and is decoded exactly once.
 * ---------------------------------------------------------------------------
 */

export const BRIDGE_VERSION = 1

const reactNativeHost = () =>
    (typeof window !== 'undefined' && window.ReactNativeWebView) || null

const legacyCordovaHost = () =>
    (typeof window !== 'undefined'
        && window.webkit
        && window.webkit.messageHandlers
        && window.webkit.messageHandlers.cordova_iab) || null

/** True when running inside either native shell. */
export function isNativeHost() {
    return Boolean(reactNativeHost() || legacyCordovaHost())
}

function send(host, payload) {
    host.postMessage(JSON.stringify(payload))
    return true
}

/**
 * Translate a v1 control message into the command the shipped Cordova app
 * understands. Returns null for messages that shell has no concept of.
 */
function toLegacyCommand(msg) {
    switch (msg.type) {
        case 'logout':
            return { command: 'logout' }
        case 'route':
            // The shipped app only reads event.data.to.name, to detect a return
            // to the login screen and close itself.
            return { command: 'navigate', to: { name: msg.name, path: msg.path } }
        default:
            // 'ready' and anything added later: the legacy shell never handled
            // these and never replies. Dropping is correct.
            return null
    }
}

/**
 * Post a control message ('ready' | 'logout' | 'route').
 * Returns true if a host received it.
 */
export function post(msg) {
    const rn = reactNativeHost()
    if (rn) return send(rn, { v: BRIDGE_VERSION, ...msg })

    const iab = legacyCordovaHost()
    if (iab) {
        const legacy = toLegacyCommand(msg)
        return legacy ? send(iab, legacy) : false
    }

    return false
}

/**
 * Hand a document to the native host.
 *
 *   intent: 'view'     -> render in the app's own PDF viewer (has a back button)
 *           'download' -> hand to the OS sheet to save/print/send
 *
 * Takes raw bytes (ArrayBuffer or Uint8Array); call sites must not pre-encode.
 * Returns true if a host received it.
 */
export function postDocument({ intent, bytes, filename, title }) {
    const rn = reactNativeHost()
    if (rn) {
        return send(rn, {
            v: BRIDGE_VERSION,
            type: intent === 'download' ? 'downloadDocument' : 'viewDocument',
            content: toBase64(bytes),
            encoding: 'base64',
            filename,
            title,
        })
    }

    const iab = legacyCordovaHost()
    if (iab) {
        // Legacy shell has no view/download distinction — everything was 'print'.
        // Double-encoded: see the encoding note at the top of this file.
        return send(iab, { command: 'print', content: toBase64(toBase64(bytes)) })
    }

    return false
}

/** Bytes (or a binary string) -> base64. */
function toBase64(input) {
    return btoa(typeof input === 'string' ? input : toBinaryString(input))
}

/**
 * Uint8Array/ArrayBuffer -> binary string, in chunks.
 *
 * String.fromCharCode.apply() over a whole multi-megabyte document throws
 * 'RangeError: Maximum call stack size exceeded'. 32k at a time stays well
 * inside the argument limit.
 */
function toBinaryString(bytes) {
    const array = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
    const CHUNK = 32768
    const parts = []
    for (let i = 0; i < array.length; i += CHUNK) {
        parts.push(String.fromCharCode.apply(null, array.subarray(i, i + CHUNK)))
    }
    return parts.join('')
}

/**
 * ready / hostInfo handshake.
 *
 * The web app posts 'ready' once per load and a modern native host replies with
 * hostInfo, which is how this side learns what the host can actually do rather
 * than assuming. A host that predates a capability simply omits it, so new message
 * types can ship without breaking older app builds.
 *
 * 'ready' is not the first message a host sees, despite the name. router.js's
 * beforeEach guard posts a 'route' while the router is being installed - before
 * app.mount() returns, and well before router.isReady() resolves and main.js posts
 * 'ready' - so the real order is route(initial path), then ready(same path), and
 * the initial path is announced twice by two different message types. The native
 * host dispatches on message type with no handshake gate, and pins that ordering
 * in app/expo/getmypaystub-v2/src/bridge/__tests__/handlers.test.ts. Do not add an
 * ordering assumption on either side.
 *
 * The legacy Cordova host never replies and never handled 'ready', so it is not
 * sent one (toLegacyCommand drops it). Absence of hostInfo means "legacy host,
 * assume print only".
 *
 * Note what this deliberately does NOT do: it does not set isInApp. That line
 * (AutoLogin.vue:22) cannot change until the legacy branch goes — see the design
 * spec §5.
 */

let capabilities = []
let lastHostInfo = null
const hostInfoListeners = new Set()

/**
 * Hand a payload to one subscriber without letting it break the others.
 *
 * A subscriber that throws is that subscriber's bug, but it must not cost the
 * remaining ones their notification, and it must not escape into the `message`
 * handler. Reported rather than swallowed; this module stays framework-free, so
 * a caller wanting the error in Sentry should wrap its own callback.
 */
function notify(listener, payload) {
    try {
        listener(payload)
    } catch (ex) {
        console.error('native-bridge: onHostInfo subscriber threw', ex)
    }
}

/** Post the one-time 'ready'. Returns true if a modern host received it. */
export function announceReady(route) {
    return post({ type: 'ready', route })
}

/**
 * What the native host reported it can do. Empty until hostInfo arrives.
 * A copy: callers must not be able to mutate this for everyone else.
 */
export function hostCapabilities() {
    return capabilities.slice()
}

/**
 * Subscribe to the hostInfo reply. Returns an unsubscribe function.
 *
 * Replays the reply if it has already arrived. hostInfo lands once, moments
 * after 'ready', so anything mounted on a lazily reached route subscribes after
 * the fact — without the replay it would never fire, and could not tell "the
 * host has not answered yet" from "the host answered before I existed".
 */
export function onHostInfo(callback) {
    hostInfoListeners.add(callback)
    if (lastHostInfo) notify(callback, lastHostInfo)
    return () => hostInfoListeners.delete(callback)
}

/** Test seam — the handshake is module state, and tests need it reset. */
export function resetHostInfo() {
    capabilities = []
    lastHostInfo = null
    hostInfoListeners.clear()
}

if (typeof window !== 'undefined') {
    // The page receives unrelated window messages (extensions, embeds, our own
    // libraries), so anything that isn't a v1 hostInfo is dropped in silence.
    //
    // No origin check, deliberately: capabilities only inform whether a bridge post
    // is attempted, and a bridge that isn't there returns false, so a forged hostInfo
    // buys nothing today.
    //
    // Do NOT "fix" that with `event.origin === window.location.origin`. The native
    // host's injected `new MessageEvent('message', {data})` carries origin === '' by
    // spec default, so that check rejects every real hostInfo and the handshake
    // silently never completes. If capabilities ever gate something consequential,
    // the two viable forms are: set an explicit origin in the injected event on the
    // native side and check for that value, or treat origin === '' as the
    // injected-dispatch signature. Neither stops a same-page script — both the host's
    // dispatch and an attacker's would be in-page.
    window.addEventListener('message', (event) => {
        let payload
        try {
            payload = JSON.parse(event.data)
        } catch {
            return
        }

        if (!payload || payload.v !== BRIDGE_VERSION || payload.type !== 'hostInfo') return

        capabilities = Array.isArray(payload.capabilities) ? payload.capabilities : []
        lastHostInfo = payload
        hostInfoListeners.forEach((listener) => notify(listener, payload))
    })
}
